import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/feature/EventCard";
import { useSeo } from "@/hooks/useSeo";
import { categories, events } from "@/mocks/content";
import { getTopEvents } from "@/utils/content";

export default function Events() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") ?? "all";
  const q = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(q);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return events.filter((item) => {
      const matchCat = cat === "all" || item.categoryId === cat;
      const matchKw =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((t) => t.toLowerCase().includes(kw));
      return matchCat && matchKw;
    });
  }, [cat, q]);

  const top = getTopEvents(5);
  const activeCat = categories.find((c) => c.id === cat);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useSeo({
    title: activeCat
      ? `${activeCat.name} | 1每日吃瓜热门事件`
      : "热门事件 | 1每日吃瓜热点事件集中盘点",
    description: activeCat
      ? `${activeCat.desc}。1每日吃瓜为你汇总${activeCat.name}方向的每日吃瓜热门事件与焦点事件脉络梳理。`
      : "1每日吃瓜热门事件频道，按剧集、综艺、电影、音乐、时尚与全网热议分类整理，支持关键词搜索，快速找到你关心的焦点事件与脉络梳理。",
    keywords: "1每日吃瓜热门事件,热门事件盘点,焦点事件,大瓜汇总,剧集,综艺,电影,音乐现场",
    path: cat === "all" ? "/events" : `/events?cat=${cat}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: activeCat ? `${activeCat.name} - 1每日吃瓜热门事件` : "1每日吃瓜热门事件",
      url: "https://77di.cn/events",
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://77di.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "热门事件",
            item: "https://77di.cn/events",
          },
        ],
      },
    },
  });

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/60 px-4 py-10 md:px-8 md:py-14">
        <Breadcrumb items={[{ label: "热门事件" }]} />
        <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          {activeCat ? activeCat.name : "热门事件"}
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {activeCat
            ? `${activeCat.desc}。下面收录了该方向的焦点事件，点击任意一条即可查看完整事件脉络。`
            : "把近期讨论度较高的焦点事件集中在这里。可以按分类筛选，也可以直接搜索关键词，找到你想了解的那一条。"}
        </p>
      </div>

      <div className="grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <form
              className="relative w-full sm:max-w-xs"
              onSubmit={(e) => {
                e.preventDefault();
                updateParam("q", keyword);
              }}
            >
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索事件或标签"
                aria-label="搜索事件或标签"
                className="w-full rounded-md border border-background-300/80 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
            </form>
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-foreground-900">{filtered.length}</strong> 条事件
            </p>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => updateParam("cat", "all")}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                cat === "all"
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
              }`}
            >
              全部
            </button>
            {categories.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => updateParam("cat", item.id)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  cat === item.id
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700"
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          <div className="mt-8 space-y-5">
            {filtered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 py-20 text-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                <p className="mt-3 text-sm text-foreground-600">
                  没有找到相关事件，换个关键词或分类试试吧。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    setParams(new URLSearchParams(), { replace: true });
                  }}
                  className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选
                </button>
              </div>
            ) : (
              filtered.map((item) => (
                <EventCard
                  key={item.slug}
                  variant="list"
                  slug={item.slug}
                  title={item.title}
                  summary={item.summary}
                  cover={item.cover}
                  category={item.category}
                  date={item.date}
                  heat={item.heat}
                  readTime={item.readTime}
                />
              ))
            )}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-6">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-price-tag-3-line text-primary-500"></i>
                内容分类
              </h2>
              <ul className="mt-4 space-y-1">
                {categories.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => updateParam("cat", item.id)}
                      className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${item.icon} text-foreground-500`}></i>
                        {item.name}
                      </span>
                      <span className="text-xs text-foreground-400">{item.count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-fire-line text-primary-500"></i>
                今日热榜
              </h2>
              <ol className="mt-4 space-y-3">
                {top.map((item, idx) => (
                  <li key={item.slug} className="flex gap-3">
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                        idx < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <Link
                      to={`/events/${item.slug}`}
                      className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors hover:text-primary-700"
                    >
                      {item.title}
                    </Link>
                  </li>
                ))}
              </ol>
              <Link
                to="/roundup"
                className="mt-5 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300/80 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                完整本周瓜单
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}